<template>
    <v-col v-bind="DASHBOARD_WIDGET_COLUMNS.LARGE">
        <v-card>
            <v-toolbar color="grey-lighten-4" density="compact" flat>
                <template #title>{{ widget.body?.title }}</template>
            </v-toolbar>
            <v-divider />
            <v-card-text>
                <v-row class="pb-1">
                    <v-col cols="12" lg="3" md="5">
                        <v-menu v-model="menuFrom" :close-on-content-click="false" transition="scale-transition" offset-y>
                            <template #activator="{ props: activatorProps }">
                                <v-text-field
                                    :model-value="filters.checkDate__gte"
                                    density="compact"
                                    hide-details
                                    label="Od:"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    clearable
                                    variant="outlined"
                                    :disabled="dashboardStore.paperNeededLoading"
                                    v-bind="activatorProps"
                                    @click:clear="clearDateFilter('checkDate__gte')"
                                />
                            </template>
                            <v-date-picker
                                :model-value="filters.checkDate__gte"
                                first-day-of-week="1"
                                @update:model-value="onDateChange('checkDate__gte', $event, 'from')"
                            />
                        </v-menu>
                    </v-col>
                    <v-col cols="12" lg="3" md="5">
                        <v-menu v-model="menuTo" :close-on-content-click="false" transition="scale-transition" offset-y>
                            <template #activator="{ props: activatorProps }">
                                <v-text-field
                                    :model-value="filters.checkDate__lte"
                                    density="compact"
                                    hide-details
                                    label="Do:"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    clearable
                                    variant="outlined"
                                    :disabled="dashboardStore.paperNeededLoading"
                                    v-bind="activatorProps"
                                    @click:clear="clearDateFilter('checkDate__lte')"
                                />
                            </template>
                            <v-date-picker
                                :model-value="filters.checkDate__lte"
                                first-day-of-week="1"
                                @update:model-value="onDateChange('checkDate__lte', $event, 'to')"
                            />
                        </v-menu>
                    </v-col>
                    <v-col cols="12" lg="1" md="2">
                        <v-btn color="secondary" icon="mdi-refresh" size="small" @click="changeFilter" />
                    </v-col>
                </v-row>

                <v-data-table
                    :headers="headers"
                    :items="dashboardStore.paperNeeded"
                    :loading="dashboardStore.paperNeededLoading"
                    :page="page"
                    :items-per-page="itemsPerPage"
                    :items-length="itemsLength"
                    density="compact"
                    item-value="paper"
                    @update:options="onOptionsUpdate"
                />
            </v-card-text>
        </v-card>
    </v-col>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { reactive, ref } from 'vue'

import { errorHandler } from '@/composables/errorHandler'
import { useDashboardStore } from '@/stores'
import type { DashboardPermission, DashboardWidget } from '@/stores/dashboard'
import { normalizeDateOnly } from '@/utils/dateUtils'
import { buildQueryString, DASHBOARD_WIDGET_COLUMNS } from '@/views/Dashboard/dashboardUtils'

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        widget: DashboardWidget
        permissions?: DashboardPermission[]
    }>(),
    { permissions: () => [] }
)

// 3. Constants
const headers = [
    { title: 'Data zamknięcia zlecenia', key: 'checkDate', sortable: false },
    { title: 'Papier', key: 'paper', sortable: false },
    { title: 'Ilość (ark)', key: 'qtyInSht', sortable: false },
    { title: 'Ilość (kg)', key: 'qtyInKgs', sortable: false }
]

// 4. Stores & top-level services
const dashboardStore = useDashboardStore()

// 5. Reactive state (refs/reactive)
const filters = reactive({ ...dashboardStore.filters })
const page = ref(1)
const itemsPerPage = ref(10)
const itemsLength = ref(0)
const menuFrom = ref(false)
const menuTo = ref(false)

// 8. Handlers / methods
async function getDashboard() {
    try {
        const response = await dashboardStore.getDashboardItem({
            item: props.widget,
            paginate: `?limit=${itemsPerPage.value}&offset=${(page.value - 1) * itemsPerPage.value}`,
            query: buildQueryString(filters).replace(/^\?/, '')
        })
        itemsLength.value = Number(response?.data?.body?.data?.count || 0)
    } catch (error) {
        errorHandler(error)
    }
}

function changeFilter() {
    dashboardStore.setDashboardFilters(filters)
    page.value = 1
    getDashboard()
}

function clearDateFilter(key: 'checkDate__gte' | 'checkDate__lte'): void {
    filters[key] = ''
}

function onDateChange(key: 'checkDate__gte' | 'checkDate__lte', value: string | Date | null, menu: 'from' | 'to'): void {
    const date = normalizeDateOnly(value)

    if (date !== null) {
        filters[key] = date
    }

    if (menu === 'from') {
        menuFrom.value = false
        return
    }

    menuTo.value = false
}

function onOptionsUpdate(options: { page: number; itemsPerPage: number }) {
    page.value = options.page
    itemsPerPage.value = options.itemsPerPage
    getDashboard()
}

getDashboard()
</script>
